export interface Prompt {
  id: string;
  title: string;
  category: PromptCategory;
  description: string;
  prompt: string;
  tags: string[];
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
}

export type PromptCategory =
  | 'Clinical Documentation'
  | 'Treatment Planning'
  | 'Patient Education'
  | 'Research & Evidence'
  | 'Administrative'
  | 'Professional Development'
  | 'Case Analysis';

export const prompts: Prompt[] = [
  // ==========================================
  // CLINICAL DOCUMENTATION (10 prompts)
  // ==========================================
  {
    id: 'doc-001',
    title: 'SOAP Note Generator',
    category: 'Clinical Documentation',
    description: 'Generate professional SOAP notes from session details',
    prompt: `Generate a professional SOAP note for this OT session:

Patient: [Age, diagnosis, setting]
Session date: [Date]
Session focus: [What you worked on]

Subjective:
- Patient statements: [What they said]
- Pain level: [If applicable]
- Mood/engagement: [Observations]

Objective:
- Activities completed: [List activities]
- Performance observations: [How they did]
- Measurements/scores: [Any assessments]
- Assistance level: [Independence, mod assist, etc.]

Assessment:
- Progress toward goals: [Which goals, progress made]
- Barriers observed: [Challenges]
- Clinical reasoning: [Your thinking]

Plan:
- Next session focus: [What's next]
- Home program: [Any assignments]
- Goal modifications: [If needed]

Please format as a professional SOAP note suitable for medical records.`,
    tags: ['documentation', 'SOAP', 'progress notes'],
    difficulty: 'Beginner'
  },
  {
    id: 'doc-002',
    title: 'Initial Evaluation Summary',
    category: 'Clinical Documentation',
    description: 'Create comprehensive initial evaluation documentation',
    prompt: `Create an initial evaluation summary for:

Patient Information:
- Demographics: [Age, diagnosis, medical history]
- Referral reason: [Why referred to OT]
- Living situation: [Home setup, support system]

Occupational Profile:
- Prior level of function: [Before current condition]
- Current concerns: [Patient/family priorities]
- Valued activities: [What matters most to them]

Performance Assessment:
- ADL status: [Current abilities and limitations]
- IADL status: [Shopping, cooking, finances, etc.]
- Mobility/transfers: [Observations]
- Cognition: [Memory, attention, problem-solving]
- Upper extremity: [ROM, strength, coordination]

Assessment Tools Used:
- [List standardized assessments and scores]

Analysis and Clinical Reasoning:
- Barriers to independence: [Identify]
- Potential for improvement: [Prognosis]
- Safety concerns: [Any risks]

Goals (SMART format):
- [Generate 3-5 appropriate goals]

Recommended Plan:
- Frequency: [Sessions per week]
- Duration: [Expected length of care]
- Focus areas: [Treatment priorities]

Format as professional evaluation documentation.`,
    tags: ['evaluation', 'initial assessment', 'documentation'],
    difficulty: 'Intermediate'
  },
  {
    id: 'doc-003',
    title: 'Progress Note - Quick Format',
    category: 'Clinical Documentation',
    description: 'Fast progress note for routine sessions',
    prompt: `Quick progress note for today's session:

Patient: [Name/ID]
Date: [Date]
Session #: [Number]

What we did: [Activities - bullet points]

How they performed: [Observations - bullet points]

Progress on goals: [Which goals addressed, progress made]

Plan for next session: [Brief plan]

Write a concise, professional progress note (3-5 sentences).`,
    tags: ['documentation', 'progress note', 'quick'],
    difficulty: 'Beginner'
  },
  {
    id: 'doc-004',
    title: 'Discharge Summary',
    category: 'Clinical Documentation',
    description: 'Comprehensive discharge documentation',
    prompt: `Create a discharge summary for:

Patient: [Demographics, diagnosis]
Treatment dates: [Start - End]
Total sessions: [Number]

Initial Status:
- [Key limitations at start of care]

Treatment Provided:
- [Summary of interventions used]

Outcomes Achieved:
- Goal 1: [Status - met/not met/progress]
- Goal 2: [Status]
- Goal 3: [Status]
- [Functional improvements with specific examples]

Current Status:
- [ADL independence level]
- [IADL abilities]
- [Safety awareness]

Recommendations:
- [Home program]
- [Equipment needs]
- [Follow-up care]
- [Referrals to other services]

Discharge Disposition: [Home, facility, etc.]

Format as professional discharge summary.`,
    tags: ['discharge', 'documentation', 'summary'],
    difficulty: 'Intermediate'
  },
  {
    id: 'doc-005',
    title: 'Prior Authorization Letter',
    category: 'Clinical Documentation',
    description: 'Justify medical necessity for insurance',
    prompt: `Write a prior authorization letter for:

Patient: [Name, DOB, ID]
Diagnosis: [Primary diagnosis]
Requested Service: [What needs approval]
Number of visits: [Requested]

Include:

Medical Necessity:
- Current functional limitations: [Specific impacts on daily life]
- Safety concerns: [Risks without treatment]
- Prior level of function: [What changed]

Clinical Rationale:
- Evidence supporting this intervention: [Research/guidelines]
- Expected outcomes: [Specific, measurable]
- Why this specific frequency/duration: [Justification]

Functional Goals:
- [3-5 SMART goals with timeframes]

Without Treatment:
- [Consequences of denial - safety, quality of life]

Professional Credentials:
- [OT credentials and experience]

Format as formal letter to insurance medical director. Use professional medical terminology while clearly explaining functional impact.`,
    tags: ['insurance', 'authorization', 'medical necessity'],
    difficulty: 'Advanced'
  },
  {
    id: 'doc-006',
    title: 'Goal Writing Assistant',
    category: 'Clinical Documentation',
    description: 'Create SMART goals from patient needs',
    prompt: `Create SMART occupational therapy goals based on:

Patient: [Age, diagnosis, setting]
Current limitations: [What they struggle with]
Patient priorities: [What matters most to them]
Timeframe: [Expected length of treatment]

For each goal, ensure:
- Specific: Clear, concrete task
- Measurable: Quantifiable criteria
- Achievable: Realistic for this patient
- Relevant: Meaningful to patient's life
- Time-bound: Clear timeframe

Create 3-5 goals covering:
- At least one ADL goal
- At least one meaningful occupation
- Include baseline and target performance

Format:
Goal 1: [Full SMART goal]
Baseline: [Current performance]
Target: [Desired performance by when]

[Repeat for each goal]`,
    tags: ['goals', 'SMART goals', 'treatment planning'],
    difficulty: 'Beginner'
  },
  {
    id: 'doc-007',
    title: 'Functional Outcomes Summary',
    category: 'Clinical Documentation',
    description: 'Summarize functional progress for reports',
    prompt: `Summarize functional outcomes for reporting period:

Patient: [Demographics]
Reporting period: [Dates]
Number of sessions: [Count]

Initial functional status:
- [Describe using FIM levels or similar]

Interventions provided:
- [Key treatment approaches used]

Functional improvements (specific examples):
- ADL: [Before → After with examples]
- Mobility: [Before → After]
- Cognition: [Before → After if applicable]
- Instrumental tasks: [Before → After]

Assistive devices/adaptations implemented:
- [Equipment or strategies]

Patient/caregiver education:
- [Topics covered]

Current status summary:
- [Overall functional level]
- [Remaining limitations]
- [Safety considerations]

Create a clear, outcome-focused summary suitable for case managers, physicians, or insurance reviewers.`,
    tags: ['outcomes', 'reporting', 'functional status'],
    difficulty: 'Intermediate'
  },
  {
    id: 'doc-008',
    title: 'Medicare Documentation Compliance',
    category: 'Clinical Documentation',
    description: 'Ensure notes meet Medicare requirements',
    prompt: `Review and enhance this documentation for Medicare compliance:

[Paste your draft note]

Ensure the enhanced version includes:

Medical Necessity:
- Clear link between diagnosis and deficits
- Functional limitations requiring skilled OT
- Why skilled therapy needed (vs. aide or family)

Skilled Services:
- Complex clinical reasoning demonstrated
- Professional judgment required
- Specific therapeutic techniques used

Measurable Progress:
- Objective data showing change
- Comparison to baseline
- Specific functional gains

Plan Justification:
- Why continued therapy needed
- Realistic timeframes
- Clear discharge criteria

Compliance Requirements:
- Certification statement
- Physician orders referenced
- Patient participation noted
- Complexity of condition addressed

Rewrite to meet Medicare documentation standards while maintaining clinical accuracy.`,
    tags: ['Medicare', 'compliance', 'documentation'],
    difficulty: 'Advanced'
  },
  {
    id: 'doc-009',
    title: 'Caregiver Training Documentation',
    category: 'Clinical Documentation',
    description: 'Document caregiver education sessions',
    prompt: `Document caregiver training session:

Patient: [Demographics]
Caregiver: [Relationship, involvement level]
Training date: [Date]

Topics Covered:
- [What was taught]

Teaching Methods:
- Demonstration: [What you showed]
- Practice: [What caregiver practiced]
- Education materials: [Handouts provided]

Caregiver Performance:
- Understanding demonstrated: [How assessed]
- Skills demonstrated: [What they can do]
- Areas needing more practice: [Challenges]

Safety Concerns Addressed:
- [Specific safety issues taught]

Home Program:
- [What caregiver should do daily/weekly]
- [When to contact therapist]

Caregiver Questions:
- [Questions asked and answered]

Follow-up Plan:
- [Next training session or check-in]

Format as professional documentation showing skilled caregiver education.`,
    tags: ['caregiver', 'education', 'training'],
    difficulty: 'Intermediate'
  },
  {
    id: 'doc-010',
    title: 'Group Session Note',
    category: 'Clinical Documentation',
    description: 'Document group therapy sessions',
    prompt: `Document group therapy session:

Group Type: [ADL, Cognitive, Social skills, etc.]
Date: [Date]
Participants: [Number, general demographics]

Session Structure:
- Topic/Theme: [Focus of session]
- Activities: [What group did]
- Duration: [Time]

Individual Patient Documentation:

Patient 1: [ID]
- Participation level: [Active, passive, resistive]
- Performance: [Specific observations]
- Progress on individual goals: [Which goals addressed]
- Social interaction: [Group dynamics observed]

[Repeat for each patient]

Group Dynamics:
- [Overall engagement]
- [Peer support/interaction]
- [Any challenges]

Clinical Reasoning:
- [Why this group benefits these patients]
- [Therapeutic value observed]

Plan:
- [Next group session focus]
- [Individual follow-up needed]

Create documentation that shows skilled group therapy and individual patient progress.`,
    tags: ['group therapy', 'documentation', 'multiple patients'],
    difficulty: 'Intermediate'
  },

  // ==========================================
  // TREATMENT PLANNING (10 prompts)
  // ==========================================
  {
    id: 'treat-001',
    title: 'Activity Analysis for Treatment',
    category: 'Treatment Planning',
    description: 'Analyze activities for therapeutic use',
    prompt: `Perform activity analysis for therapeutic use:

Activity: [Name of activity]
Patient population: [Who this is for]
Primary goals: [What you want to address]

Please analyze:

Physical Demands:
- Muscle groups used
- ROM requirements
- Strength requirements
- Endurance needed
- Coordination/fine motor

Cognitive Demands:
- Attention required
- Memory components
- Problem-solving
- Executive function
- Sequencing

Sensory Requirements:
- Vision
- Tactile
- Proprioception
- Auditory

Social/Emotional:
- Interaction required
- Emotional regulation
- Communication

Performance Skills Addressed:
- [List applicable skills]

How to Grade:
- Make easier: [Modifications to decrease challenge]
- Make harder: [Progressions to increase challenge]

Therapeutic Benefits:
- [How this addresses patient goals]

Provide detailed analysis suitable for treatment planning.`,
    tags: ['activity analysis', 'treatment planning', 'grading'],
    difficulty: 'Intermediate'
  },
  {
    id: 'treat-002',
    title: 'Home Exercise Program',
    category: 'Treatment Planning',
    description: 'Create customized home programs',
    prompt: `Create a home exercise program for:

Patient: [Age, diagnosis, limitations]
Goals: [What we're working toward]
Available equipment: [What they have at home]
Time available: [How much time daily]
Caregiver support: [Who can help]

Create program with:

Exercise 1: [Name]
- Purpose: [What it helps]
- Instructions: [Step-by-step, simple language]
- Frequency: [How often]
- Reps/duration: [How much]
- Precautions: [What to avoid]

[Repeat for 5-7 exercises total]

Schedule Suggestion:
- [When to do exercises]
- [How to fit into daily routine]

Progress Tracking:
- [How to know it's working]
- [When to advance]

Safety:
- [Warning signs to stop]
- [When to contact therapist]

Format as clear, patient-friendly handout with simple language (6th-8th grade reading level).`,
    tags: ['home program', 'exercise', 'patient education'],
    difficulty: 'Beginner'
  },
  {
    id: 'treat-003',
    title: 'Adaptive Equipment Recommendations',
    category: 'Treatment Planning',
    description: 'Recommend appropriate assistive devices',
    prompt: `Recommend adaptive equipment for:

Patient: [Age, diagnosis, specific limitations]
Environment: [Home setup, accessibility]
Goals: [What tasks need support]
Budget considerations: [Insurance, out-of-pocket limits]

For each recommendation, provide:

Problem/Task: [What's difficult]

Recommended Equipment: [Specific item]
- Purpose: [How it helps]
- Features to look for: [Important qualities]
- Price range: [Typical cost]
- Where to buy: [Sources]
- Training needed: [How to use]
- Alternatives: [Other options if this doesn't work]

[Provide 3-5 recommendations]

Priority Order:
1. [Most important item - why]
2. [Second priority - why]
3. [Third priority - why]

Insurance Coverage:
- [Which items typically covered]
- [Documentation needed for approval]

Trials/Returns:
- [How to test before buying]

Create practical, actionable equipment plan.`,
    tags: ['adaptive equipment', 'assistive devices', 'recommendations'],
    difficulty: 'Intermediate'
  },
  {
    id: 'treat-004',
    title: 'Sensory Diet Design',
    category: 'Treatment Planning',
    description: 'Create personalized sensory programs',
    prompt: `Design a sensory diet for:

Child/Adult: [Age, diagnosis]
Sensory profile: [Seeking/avoiding patterns]
- Visual: [Preferences]
- Auditory: [Sensitivities/needs]
- Tactile: [Responses]
- Vestibular: [Seeking/avoiding]
- Proprioceptive: [Needs]

Challenging behaviors: [What we're addressing]
Environment: [School/home/work]
Schedule: [Daily routine]

Create sensory diet with:

Morning Routine (Wake up to school/work):
- Activities: [3-4 sensory activities]
- Purpose: [What each provides - alerting/calming/organizing]
- Duration: [How long]

Midday (Transitions, challenging times):
- Activities: [Strategies for regulation]
- Cues: [When to use]
- Tools needed: [Any equipment]

Afternoon/Evening:
- Activities: [Calming/organizing activities]
- Wind-down strategies: [Before bed]

Sensory Tools Kit:
- [Portable items for regulation]
- [When to use each]

Environmental Modifications:
- [Changes to space for sensory needs]

Progress Monitoring:
- [What to track]
- [Signs it's working]

Create practical, evidence-based sensory program.`,
    tags: ['sensory', 'sensory diet', 'regulation'],
    difficulty: 'Advanced'
  },
  {
    id: 'treat-005',
    title: 'Cognitive Strategy Training',
    category: 'Treatment Planning',
    description: 'Design cognitive compensation strategies',
    prompt: `Design cognitive compensation strategies for:

Patient: [Age, diagnosis]
Cognitive deficits: [Memory, attention, executive function, etc.]
Goals: [What tasks they need to do]
Insight level: [Awareness of deficits]

Create strategy training plan:

Memory Strategies:
- External aids: [Calendars, apps, notebooks - specific recommendations]
- Routines: [Structured approaches to reduce memory load]
- Mnemonics: [Memory tricks for important information]

Attention Strategies:
- Environmental: [Reduce distractions]
- Task management: [Break down complex tasks]
- Self-monitoring: [Check and redirect]

Executive Function Strategies:
- Planning tools: [How to organize tasks]
- Problem-solving framework: [Step-by-step approach]
- Initiation supports: [Getting started on tasks]

Implementation Plan:
- Training sequence: [What to teach first, second, etc.]
- Practice activities: [How to practice each strategy]
- Generalization: [Apply to real-life tasks]

Caregiver Training:
- [How caregivers can support strategy use]
- [When to prompt, when to allow struggle]

Success Metrics:
- [How to know strategies are working]

Provide detailed, evidence-based cognitive rehabilitation plan.`,
    tags: ['cognitive', 'strategies', 'compensation'],
    difficulty: 'Advanced'
  },
  {
    id: 'treat-006',
    title: 'Fine Motor Development Plan',
    category: 'Treatment Planning',
    description: 'Progressive fine motor skill building',
    prompt: `Create fine motor development plan for:

Child: [Age]
Current level: [What they can do]
Challenges: [Specific difficulties]
Goals: [Target skills - handwriting, self-care, etc.]

Developmental Progression:

Foundation Skills (if needed):
- Shoulder stability: [Activities]
- Core strength: [Exercises]
- Bilateral coordination: [Tasks]

Pre-Writing Skills:
- Pre-grasp activities: [What to work on]
- Grasp patterns: [Development sequence]
- Hand strengthening: [Fun activities]
- In-hand manipulation: [Skills to practice]

Writing Readiness:
- Pencil grasp development: [Progression]
- Pre-writing strokes: [Which shapes, in what order]
- Visual-motor integration: [Activities]

Self-Care Skills:
- Dressing: [Fastener progression]
- Feeding: [Utensil use development]
- Grooming: [Skills appropriate for age]

Activity Ideas (fun, motivating):
- [5-10 specific activity suggestions with materials needed]

Weekly Schedule:
- [How to distribute practice throughout week]

Parent Collaboration:
- [How parents can support at home]
- [What to practice daily]

Progress Monitoring:
- [Milestones to watch for]
- [When to advance]

Create engaging, play-based plan suitable for children.`,
    tags: ['fine motor', 'pediatric', 'development'],
    difficulty: 'Intermediate'
  },
  {
    id: 'treat-007',
    title: 'Energy Conservation Plan',
    category: 'Treatment Planning',
    description: 'Strategies for fatigue management',
    prompt: `Create energy conservation plan for:

Patient: [Age, diagnosis causing fatigue]
Fatigue patterns: [When worst, what triggers]
Essential daily tasks: [Must-do activities]
Support available: [Who can help]

Energy Conservation Principles Applied:

Planning & Prioritizing:
- Daily schedule: [How to plan energy use]
- Task prioritization: [What's essential]
- Delegation: [What others can do]

Pacing:
- Work-rest balance: [Specific ratios]
- Activity modification: [How to adapt tasks]
- Break strategies: [When and how to rest]

Positioning:
- Seated activities: [Which tasks to do sitting]
- Supportive setup: [Workstation modifications]
- Ergonomics: [Reduce energy waste]

Organization:
- Home setup: [Reduce unnecessary movement]
- Supplies storage: [Strategic placement]
- Workflow: [Efficient task sequencing]

Task Simplification:
- [Specific modifications for challenging tasks]
- [Equipment to reduce effort]

Weekly Plan:
- High-energy activities: [When to schedule]
- Essential rest times: [Protected breaks]
- Flexible tasks: [What can be rescheduled]

Self-Monitoring:
- Fatigue scale: [How to track]
- Warning signs: [When to stop]
- Recovery strategies: [How to bounce back]

Provide practical, detailed fatigue management plan.`,
    tags: ['energy conservation', 'fatigue', 'chronic illness'],
    difficulty: 'Intermediate'
  },
  {
    id: 'treat-008',
    title: 'Social Skills Training Plan',
    category: 'Treatment Planning',
    description: 'Develop social interaction skills',
    prompt: `Design social skills training plan for:

Client: [Age, diagnosis]
Current social challenges: [Specific difficulties]
Goals: [Desired social outcomes]
Environment: [School, work, community]

Skill Development Sequence:

Foundational Skills:
- Eye contact: [Appropriate levels for context]
- Personal space: [Distance awareness]
- Body language reading: [Understanding others]
- Facial expression recognition: [Emotions]

Conversation Skills:
- Initiating: [How to start conversations]
- Turn-taking: [Give and take in dialogue]
- Topic maintenance: [Staying on subject]
- Asking questions: [Showing interest]
- Listening: [Active listening strategies]
- Ending conversations: [Polite exits]

Friendship Skills:
- Joining groups: [Entry strategies]
- Sharing: [Taking turns, compromise]
- Cooperation: [Working together]
- Handling conflict: [Resolution strategies]
- Empathy: [Perspective-taking]

Situational Skills:
- Classroom/workplace: [Context-appropriate behavior]
- Social events: [Party/gathering skills]
- Public spaces: [Community expectations]

Teaching Methods:
- Direct instruction: [What to teach explicitly]
- Role-play scenarios: [Practice situations]
- Video modeling: [Examples to watch]
- Social stories: [Scripts for situations]
- Real-life practice: [Generalization opportunities]

Progress Tracking:
- Observable behaviors: [What to measure]
- Frequency tracking: [Data collection]
- Generalization check: [Use in natural settings]

Create structured, evidence-based social skills curriculum.`,
    tags: ['social skills', 'autism', 'behavioral'],
    difficulty: 'Advanced'
  },
  {
    id: 'treat-009',
    title: 'Return to Work Planning',
    category: 'Treatment Planning',
    description: 'Support successful work return after injury',
    prompt: `Create return-to-work plan for:

Client: [Age, occupation]
Injury/condition: [Diagnosis, limitations]
Job demands: [Physical, cognitive requirements]
Employer accommodations: [What's possible]
Timeline: [Expected return date]

Work Capacity Assessment:

Current Abilities:
- Physical: [Strength, endurance, ROM]
- Cognitive: [Attention, memory, problem-solving]
- Functional: [Can perform which work tasks]

Job Demand Analysis:
- Essential functions: [Must-be-able-to-do]
- Physical demands: [Lifting, standing, reaching, etc.]
- Cognitive demands: [Complexity, multitasking]
- Environmental factors: [Noise, lighting, hazards]

Gap Analysis:
- [Current abilities vs. job requirements]
- [Specific barriers to return]

Treatment Plan to Bridge Gaps:
- Strength/conditioning: [Targeted exercises]
- Endurance building: [Progressive activities]
- Work simulation: [Job-specific tasks]
- Cognitive training: [If needed]

Graduated Return-to-Work:
- Phase 1: [Modified duties, reduced hours]
- Phase 2: [Progressive return to full duty]
- Phase 3: [Full return with monitoring]

Recommended Accommodations:
- Temporary: [During transition]
- Permanent: [If needed long-term]
- Equipment/tools: [Assistive devices for work]

Communication Plan:
- Employer: [What to share, when]
- Healthcare team: [Coordination needed]
- Worker: [Education on safe return]

Success Metrics:
- [How to measure readiness]
- [Red flags for premature return]

Create comprehensive, evidence-based return-to-work plan.`,
    tags: ['return to work', 'occupational rehab', 'work hardening'],
    difficulty: 'Advanced'
  },
  {
    id: 'treat-010',
    title: 'Pain Management Strategies',
    category: 'Treatment Planning',
    description: 'Holistic chronic pain management approach',
    prompt: `Design pain management plan for:

Patient: [Age, pain condition]
Pain characteristics: [Type, location, severity]
Current pain level: [0-10 scale]
Impact on function: [What they can't do]
Current pain management: [Medications, other treatments]

Comprehensive OT Pain Management:

Activity Modification:
- Joint protection: [Specific techniques for their condition]
- Ergonomics: [Work/home setup changes]
- Pacing: [Activity-rest balance]
- Movement patterns: [Safer ways to move]

Therapeutic Activities:
- Gentle mobilization: [Appropriate exercises]
- Strengthening: [Support painful areas]
- Flexibility: [ROM activities]
- Desensitization: [If appropriate]

Sensory Modalities:
- Heat: [When and how to use]
- Cold: [Applications]
- TENS: [If appropriate]
- Other: [Massage, compression, etc.]

Cognitive-Behavioral Strategies:
- Pain education: [Understanding pain science]
- Relaxation techniques: [Specific methods]
- Mindfulness: [Pain acceptance practices]
- Cognitive reframing: [Thought patterns]

Environmental Modifications:
- Sleep: [Positioning, sleep hygiene]
- Seating: [Supportive furniture]
- Tools: [Adaptive equipment to reduce pain]

Lifestyle Integration:
- Activity scheduling: [Best times for different tasks]
- Flare-up management: [When pain spikes]
- Baseline establishment: [Sustainable activity level]

Self-Management Tools:
- Pain tracking: [What to monitor]
- Trigger identification: [Pattern recognition]
- Self-advocacy: [Communicating with providers]

Create holistic, patient-centered pain management plan.`,
    tags: ['pain management', 'chronic pain', 'self-management'],
    difficulty: 'Advanced'
  },

  // ==========================================
  // PATIENT EDUCATION (10 prompts)
  // ==========================================
  {
    id: 'edu-001',
    title: 'Plain Language Health Handout',
    category: 'Patient Education',
    description: 'Create easy-to-understand patient materials',
    prompt: `Create patient education handout about [topic] in plain language.

Target audience: [Age, diagnosis, literacy level]
Reading level: 6th-8th grade
Length: 1-2 pages

Content to include:
- What is [condition/topic]: [Simple explanation]
- Why this matters: [Impact on daily life]
- What to do: [3-5 actionable steps]
- What to avoid: [Common mistakes]
- When to call for help: [Warning signs]

Format requirements:
- Clear headers
- Short sentences (15-20 words max)
- Bullet points for lists
- Active voice ("Do this" not "This should be done")
- Bold important points
- Leave space for images (I'll add later)

Tone: Encouraging and empowering (not scary or limiting)

Avoid:
- Medical jargon
- Complex explanations
- Overwhelming detail
- Negative focus

Make it practical, positive, and actionable for patients.`,
    tags: ['patient education', 'handout', 'plain language'],
    difficulty: 'Beginner'
  },
  {
    id: 'edu-002',
    title: 'Home Safety Checklist',
    category: 'Patient Education',
    description: 'Create comprehensive home safety assessment',
    prompt: `Create home safety checklist for:

Population: [Elderly, post-stroke, vision impaired, etc.]
Primary concerns: [Falls, burns, medication safety, etc.]

Create room-by-room checklist:

GENERAL (All Rooms):
□ [Safety item to check]
□ [Another item]
[5-7 items per category]

BATHROOM:
□ [Specific bathroom hazards]
□ [Modifications needed]

BEDROOM:
□ [Sleep safety items]
□ [Emergency preparedness]

KITCHEN:
□ [Cooking safety]
□ [Organization for safety]

STAIRS/HALLWAYS:
□ [Mobility safety]
□ [Lighting]

OUTDOORS:
□ [Entrance safety]
□ [Pathway hazards]

For each checked item that needs fixing:
- Why it's a hazard: [Brief explanation]
- How to fix it: [Specific solution]
- Priority: [High/Medium/Low]
- Approximate cost: [If applicable]

Include:
- Resources for modifications
- When to call professional help
- Free/low-cost safety solutions

Make it comprehensive but not overwhelming - prioritize most critical safety issues.`,
    tags: ['home safety', 'falls prevention', 'checklist'],
    difficulty: 'Intermediate'
  },
  {
    id: 'edu-003',
    title: 'Exercise Instruction Sheet',
    category: 'Patient Education',
    description: 'Clear exercise instructions with safety',
    prompt: `Create exercise instruction sheet for [body part/goal].

Exercise name: [Descriptive name]
Purpose: [What this helps - in patient-friendly language]
Target: [Who this is for]

For each exercise:

STARTING POSITION:
- Sit or stand: [Where to be]
- Body setup: [How to position yourself]
- What you need: [Equipment or props]

STEP-BY-STEP INSTRUCTIONS:
1. [First movement - clear, simple]
2. [Next movement]
3. [Return to start]

HOW MUCH:
- Repetitions: [How many times]
- Sets: [How many rounds]
- Hold time: [If applicable]
- How often: [Times per day/week]

WHAT IT SHOULD FEEL LIKE:
- Good: [Normal sensations]
- Stop if: [Warning signs]

MAKE IT EASIER:
- [Modification for beginners]

MAKE IT HARDER:
- [Progression when ready]

COMMON MISTAKES:
- [What not to do]
- [How to correct]

SAFETY:
- When to stop: [Red flags]
- Contact therapist if: [Concerning signs]

Include checkbox: □ Completed today

Use simple language, active voice, and clear descriptions suitable for patients to follow at home without supervision.`,
    tags: ['exercise', 'instruction', 'home program'],
    difficulty: 'Beginner'
  },
  {
    id: 'edu-004',
    title: 'Adaptive Technique Training',
    category: 'Patient Education',
    description: 'Teach compensatory strategies for daily tasks',
    prompt: `Create training guide for [specific task] using adaptive techniques.

Task: [Getting dressed, bathing, cooking, etc.]
For: [Diagnosis, specific limitation]
Adaptation strategy: [One-handed, energy conservation, etc.]

Current challenge:
- [Why this task is difficult]

New technique:

SETUP:
- Gather these items: [What you need]
- Best location: [Where to do this]
- Positioning: [How to set up]

STEP-BY-STEP METHOD:
1. [First step - detailed instruction]
2. [Second step]
3. [Continue through task]
[Number steps clearly, keep each step simple]

SAFETY TIPS:
- [Specific safety considerations]
- [What to watch out for]

TIPS FOR SUCCESS:
- [Helpful hints]
- [Common problems and solutions]

PRACTICE PLAN:
- Start with: [Easier version or part of task]
- Progress to: [Full task]
- Expected timeframe: [How long to master]

EQUIPMENT NEEDED:
- [Specific adaptive equipment]
- [Where to get it]
- [Cost range]

WHEN TO ASK FOR HELP:
- [Tasks that still need assistance]
- [How to stay safe]

Make it encouraging and practical, with realistic expectations and clear success steps.`,
    tags: ['adaptive techniques', 'compensation', 'ADL training'],
    difficulty: 'Intermediate'
  },
  {
    id: 'edu-005',
    title: 'Joint Protection Education',
    category: 'Patient Education',
    description: 'Teach joint protection principles',
    prompt: `Create joint protection education for [condition - arthritis, etc.].

Reading level: 8th grade
Length: 1-2 pages

WHAT IS JOINT PROTECTION?
- [Simple explanation]
- [Why it matters]

THE 5 KEY PRINCIPLES:

1. USE LARGER, STRONGER JOINTS
   - What this means: [Explanation]
   - Example: [Specific daily task]
   - Instead of: [Wrong way]
   - Try: [Better way]

2. AVOID STAYING IN ONE POSITION
   - What this means: [Explanation]
   - Example: [Specific situation]
   - Instead of: [Sustained grip/position]
   - Try: [Frequent position changes]

[Continue with principles 3-5]

DAILY ROUTINE TIPS:

Morning:
✓ [Specific tip for morning activities]
✓ [Another morning tip]

Kitchen:
✓ [Cooking/kitchen tips]
✓ [Another kitchen tip]

Throughout Day:
✓ [General daily tips]

WHAT TO AVOID:
✗ [Harmful activity - with explanation why]
✗ [Another thing to avoid]

HELPFUL TOOLS:
- [Specific adaptive equipment that helps]
- [Where to get it]

Remember: Small changes add up! You don't have to do everything perfectly. Start with one or two changes and build from there.

Make it empowering and practical, not limiting or scary.`,
    tags: ['joint protection', 'arthritis', 'patient education'],
    difficulty: 'Beginner'
  },
  {
    id: 'edu-006',
    title: 'Medication Management System',
    category: 'Patient Education',
    description: 'Organize medication routines safely',
    prompt: `Create medication management guide for:

Patient: [Age, cognitive status]
Number of medications: [How many]
Timing complexity: [Multiple times per day, varying schedules]
Challenges: [Memory, vision, dexterity, etc.]

SYSTEM SETUP:

Organization Method:
- [Recommend specific system - pill organizer type, app, etc.]
- Why this system: [Matches their abilities]
- Where to get: [Source and cost]

Daily Routine Integration:

MORNING MEDICATIONS:
- Linked to: [Daily activity - breakfast, brushing teeth]
- Storage location: [Where to keep]
- Reminder: [Alarm, visual cue, etc.]

[Repeat for other times of day]

FILLING SYSTEM:
- When to fill: [Weekly, Sunday evening, etc.]
- How to fill: [Step-by-step for their organizer]
- Checking method: [Avoid errors]
- Who helps: [Caregiver role if needed]

TRACKING:
- [Simple method to track if taken - checklist, app, etc.]
- [What to do if dose missed]

SAFETY:
- Storage: [Safe, accessible location]
- Disposal: [Old/expired medications]
- Emergency info: [List of current meds to carry]

REFILL SYSTEM:
- [How to track when refills needed]
- [Reminder method]
- [Pharmacy coordination]

QUESTIONS TO ASK DOCTOR/PHARMACIST:
- [Important questions about their medications]

RED FLAGS:
- [When to call doctor about medication issues]

Make it simple, personalized, and foolproof as possible.`,
    tags: ['medication management', 'safety', 'organization'],
    difficulty: 'Intermediate'
  },
  {
    id: 'edu-007',
    title: 'Caregiver Instruction Guide',
    category: 'Patient Education',
    description: 'Train family caregivers on assistance techniques',
    prompt: `Create caregiver training guide for helping with [specific task/care need].

Patient: [Diagnosis, level of assistance needed]
Caregiver: [Relationship, experience level]
Task: [Transfers, bathing, feeding, etc.]

UNDERSTANDING THE CHALLENGE:

What the patient experiences:
- [Physical limitations]
- [Cognitive/communication issues]
- [Emotional aspects]

What they CAN still do:
- [Preserve independence - emphasize this]
- [Encourage these abilities]

YOUR ROLE AS CAREGIVER:

How much help to give:
- [Specific level - standby, minimal assist, mod assist, etc.]
- [When to step in vs. when to wait]

STEP-BY-STEP ASSISTANCE:

Setup:
- [Prepare environment]
- [Gather supplies]
- [Position yourself safely]

The Process:
1. [First step - your action and cuing]
2. [Next step]
3. [Continue through task]

For each step:
- What you do: [Your physical assistance]
- What you say: [Verbal cues]
- What to watch for: [Safety, patient response]

ENCOURAGING INDEPENDENCE:

What to say:
- Helpful: [Positive, encouraging phrases]
- Not helpful: [What to avoid saying]

How to give feedback:
- [Positive reinforcement]
- [Constructive guidance]

TAKING CARE OF YOURSELF:

Body mechanics:
- [How to avoid injury]
- [Safe lifting/transfer techniques]

When to take breaks:
- [Recognize your limits]
- [Respite care options]

SAFETY CONCERNS:

Watch for these signs:
- [Patient declining]
- [Safety issues]
- [When to call therapist]

Emergency plan:
- [What to do if patient falls, etc.]

You're doing important work. These techniques will help both you and [patient name] stay safe and successful.`,
    tags: ['caregiver training', 'family education', 'assistance'],
    difficulty: 'Intermediate'
  },
  {
    id: 'edu-008',
    title: 'Condition Explanation for Patients',
    category: 'Patient Education',
    description: 'Explain diagnoses in patient-friendly terms',
    prompt: `Explain [diagnosis/condition] to patients in simple terms.

Reading level: 6th-8th grade
Tone: Informative but reassuring
Length: 1-2 pages

WHAT IS [CONDITION]?
- [Simple definition]
- [What happens in the body - plain language]
- [Why it occurs - brief, not overwhelming]

COMMON SYMPTOMS:
You might notice:
• [Symptom 1 - how it feels]
• [Symptom 2 - daily impact]
• [Symptom 3]
[3-5 main symptoms]

HOW IT AFFECTS DAILY LIFE:
- [Impact on mobility, if applicable]
- [Impact on self-care]
- [Impact on work/school]
- [Social/emotional effects]

WHAT OT CAN HELP WITH:
We work on:
✓ [Specific functional goal]
✓ [Another area OT addresses]
✓ [Third area]

Not scary medical jargon - real life activities you care about.

TREATMENT OPTIONS:
- OT interventions: [Brief overview]
- Other treatments: [PT, medications, etc.]
- What you can do: [Self-management]

SELF-MANAGEMENT STRATEGIES:
Daily habits that help:
• [Practical tip 1]
• [Practical tip 2]
• [Practical tip 3]

WHEN TO SEEK MEDICAL HELP:
Call your doctor if:
- [Serious symptom 1]
- [Warning sign 2]
- [Emergency situation]

POSITIVE OUTLOOK:
[Realistic hope - what improvement looks like, what's possible]

RESOURCES:
- [Support groups]
- [Websites - reliable only]
- [Books or materials]

Make it balanced - acknowledge challenges but emphasize possibilities and patient control.`,
    tags: ['condition education', 'diagnosis', 'patient information'],
    difficulty: 'Beginner'
  },
  {
    id: 'edu-009',
    title: 'Sensory Strategies for Parents',
    category: 'Patient Education',
    description: 'Teach parents sensory regulation techniques',
    prompt: `Create parent guide for sensory strategies for [child's sensory challenges].

Child: [Age]
Sensory profile: [Seeking, avoiding, specific sensitivities]
Challenging behaviors: [What prompts this guide]
Setting: [Home, school, community]

UNDERSTANDING YOUR CHILD'S SENSORY NEEDS:

What you're seeing:
- [Behavior] might mean [sensory need]
- [Another behavior] could mean [different sensory need]

Why this happens:
- [Simple sensory processing explanation]
- [Not about behavior or discipline]

SENSORY STRATEGIES TO TRY:

CALMING activities (when overstimulated):
• [Specific activity 1]
  - How to do it: [Simple instructions]
  - When to use: [Situations]
  - Materials needed: [What you need]

• [Calming activity 2-3]

ALERTING activities (when under-stimulated):
• [Specific activity 1]
  - How to do it
  - When to use

• [Alerting activity 2-3]

ORGANIZING activities (for focus):
• [Activity that helps regulation]

DAILY ROUTINE INTEGRATION:

Morning (getting ready for school):
- [Specific sensory strategy]
- [Why it helps morning routine]

School/During day:
- [Portable sensory tools]
- [When to use]

After school (transition/homework):
- [Regulatory activities]

Bedtime:
- [Calming sensory routine]

SENSORY TOOLKIT (items to keep handy):
- [Fidget items]
- [Oral motor tools]
- [Movement options]
- [Calming items]

ENVIRONMENT MODIFICATIONS:

At home:
- [Changes to reduce sensory overwhelm]
- [Designated calm space]

In public:
- [Preparation strategies]
- [Emergency tools to carry]

READING YOUR CHILD'S CUES:

Early signs of overwhelm:
- [What to watch for]
- [When to intervene]

Full meltdown prevention:
- [Step in before this point]
- [De-escalation strategies]

WHAT TO AVOID:
✗ [Punishment for sensory-driven behavior]
✗ [Forcing uncomfortable sensory experiences]

Remember: You're learning together. These strategies may need adjustment as you discover what works best for your child. Trust your observations and your child's feedback.`,
    tags: ['sensory', 'parent education', 'pediatric'],
    difficulty: 'Intermediate'
  },
  {
    id: 'edu-010',
    title: 'Community Resource Guide',
    category: 'Patient Education',
    description: 'Connect patients to local support and resources',
    prompt: `Create community resource guide for [diagnosis/population/need].

Population: [Who this is for]
Geographic area: [City/region]
Primary needs: [What support they need]

MEDICAL/THERAPY RESOURCES:

Specialized Clinics:
- [Facility name]
  Contact: [How to reach]
  Services: [What they provide]
  Insurance: [What's accepted]

[List 3-5 key medical resources]

EQUIPMENT/SUPPLIES:

Where to get [adaptive equipment]:
- [Local medical supply stores]
- [Online sources - reputable]
- [Insurance DME providers]
- [Free loan programs if available]

SUPPORT GROUPS:

In-person groups:
- [Group name]
  Meets: [When/where]
  Contact: [How to join]

Online communities:
- [Vetted, supportive online groups]

FINANCIAL ASSISTANCE:

Help with medical costs:
- [Programs available]
- [Eligibility requirements]
- [How to apply]

Equipment assistance:
- [Funding sources for adaptive equipment]

TRANSPORTATION:

Getting to appointments:
- [Accessible transit options]
- [Medical transport services]
- [Volunteer driver programs]

HOME MODIFICATIONS:

Contractors experienced with accessibility:
- [Local contractors]
- [Programs that fund modifications]

LEGAL/ADVOCACY:

Know your rights:
- [ADA resources]
- [Disability rights organizations]
- [Legal aid if needed]

CAREGIVER SUPPORT:

Resources for family:
- [Respite care]
- [Caregiver support groups]
- [Educational programs]

RECREATION/SOCIAL:

Adaptive activities:
- [Accessible recreation programs]
- [Inclusive social opportunities]

OTHER HELPFUL RESOURCES:
- [Anything else relevant to this population]

HOW TO USE THIS GUIDE:
- [Tips for accessing resources]
- [What questions to ask]
- [How to advocate for yourself]

Keep this list handy. You don't have to do everything alone - these resources are here to help.

Update with current, local information for your area.`,
    tags: ['resources', 'community', 'support'],
    difficulty: 'Intermediate'
  },

  // ==========================================
  // RESEARCH & EVIDENCE (8 prompts)
  // ==========================================
  {
    id: 'research-001',
    title: 'Literature Search Strategy',
    category: 'Research & Evidence',
    description: 'Find relevant research quickly',
    prompt: `Help me find research on [topic/intervention/diagnosis].

Clinical question:
- Patient/Population: [Who]
- Intervention: [What you're considering]
- Comparison: [Compared to what, if applicable]
- Outcome: [What you want to achieve]

Please provide:

SEARCH STRATEGY:
- Keywords to use: [Specific terms]
- Databases to search: [PubMed, OTseeker, Cochrane, etc.]
- Filters to apply: [Date range, study type]

SUMMARY OF CURRENT EVIDENCE:
- Key findings from recent research (last 5 years preferred)
- Strength of evidence: [Strong/Moderate/Limited/Emerging]
- Effect sizes if available
- Consistency across studies

CLINICAL BOTTOM LINE:
- What the evidence supports
- Quality of evidence
- Applicability to my practice setting: [Describe setting]
- Gaps in research

PRACTICAL IMPLICATIONS:
- Should I use this intervention?
- Recommended dosage/frequency from research
- Patient populations most likely to benefit
- Contraindications or precautions

TOP 3-5 ARTICLES TO READ:
- [Citation with brief summary of each]

Make it practical and actionable for clinical decision-making.`,
    tags: ['research', 'evidence', 'literature search'],
    difficulty: 'Advanced'
  },
  {
    id: 'research-002',
    title: 'Research Article Summary',
    category: 'Research & Evidence',
    description: 'Summarize research for clinical application',
    prompt: `Summarize this research article for clinical use:

Article: [Title, Authors, Journal, Year]
[Paste abstract or key details if available]

Please provide:

STUDY OVERVIEW:
- Research design: [RCT, cohort, case study, etc.]
- Sample size and population
- Intervention described
- Comparison/control group
- Study duration

KEY FINDINGS:
- Primary outcomes
- Statistical significance (p-values)
- Clinical significance: [Is the change meaningful?]
- Effect sizes

STRENGTHS & LIMITATIONS:
- What makes this study reliable
- What limits its application
- Risk of bias

CLINICAL IMPLICATIONS FOR OT:
- How to apply findings to practice
- Which patients this applies to
- Practical implementation tips
- What needs to be adapted for your setting

BOTTOM LINE:
- One-sentence takeaway
- Recommendation: [Adopt/Consider/Wait for more evidence]
- Level of evidence: [Grade]

Reading time: Keep summary to 3-5 minutes maximum

Make it scannable and actionable for busy clinicians.`,
    tags: ['research summary', 'evidence', 'critical appraisal'],
    difficulty: 'Intermediate'
  },
  {
    id: 'research-003',
    title: 'Evidence for Insurance Justification',
    category: 'Research & Evidence',
    description: 'Support treatment with research evidence',
    prompt: `I need evidence to justify [intervention] for insurance authorization.

Patient: [De-identified description, diagnosis]
Intervention requested: [Specific approach]
Insurance concern: [Why they might deny]

Please provide:

EVIDENCE SUMMARY:
- Research supporting effectiveness
- Level of evidence: [Cite specific studies if possible]
- Typical outcomes and timeframes
- Cost-effectiveness data if available

MEDICAL NECESSITY ARGUMENT:
- Current functional limitations: [Specific impacts on daily life]
- Safety concerns without treatment: [Risks]
- Functional impact without treatment
- Why alternatives are insufficient

PROFESSIONAL STANDARDS:
- Clinical practice guideline support
- Professional organization position statements (AOTA, etc.)
- Standard of care in the field

EXPECTED OUTCOMES:
- Specific, measurable functional gains
- Timeframe for improvement
- Long-term benefit

RISK VS. BENEFIT:
- Benefits of treatment
- Risks of denying treatment
- No less costly alternatives available

Format as:
- Brief, persuasive summary suitable for medical director review
- Citation-ready references
- Focus on functional outcomes (not just impairment)
- Professional medical terminology

Suitable for prior authorization or appeal letter.`,
    tags: ['insurance', 'medical necessity', 'evidence'],
    difficulty: 'Advanced'
  },
  {
    id: 'research-004',
    title: 'Outcome Measure Selection',
    category: 'Research & Evidence',
    description: 'Choose appropriate assessment tools',
    prompt: `Help me select an outcome measure for [goal/diagnosis/population].

Purpose: [Progress tracking/initial eval/discharge planning/research]

Patient factors:
- Diagnosis: [Condition]
- Age: [Range]
- Setting: [Where administered]
- Cognitive level: [Consideration]
- Time available: [For assessment]

Criteria for selection:
- Valid and reliable for this population
- Responsive to change
- Clinically meaningful
- Feasible in my setting
- Not too burdensome for patient

Please recommend:

TOP 3 OUTCOME MEASURES:

Measure 1: [Name]
- What it assesses: [Brief description]
- Psychometric properties: [Validity, reliability, responsiveness]
- Administration time: [How long]
- Scoring/interpretation: [How to use results]
- Minimal Detectable Change (MDC): [If available]
- Minimal Clinically Important Difference (MCID): [If available]
- Pros: [Advantages]
- Cons: [Limitations]
- Cost/access: [Free, purchase required, training needed]

[Repeat for Measures 2 and 3]

RECOMMENDATION:
- Best fit for my situation: [Which one and why]
- Runner-up: [Alternative if first choice doesn't work]

NORMATIVE DATA:
- Where to find norms for comparison
- Age/diagnosis-specific norms available

RESOURCES:
- Where to obtain the measure
- Training requirements
- Published cutoff scores

Help me choose the most appropriate measure for tracking meaningful change.`,
    tags: ['outcome measures', 'assessment', 'evidence'],
    difficulty: 'Advanced'
  },
  {
    id: 'research-005',
    title: 'Evidence Summary for Team',
    category: 'Research & Evidence',
    description: 'Create brief for team meetings or in-services',
    prompt: `Create a one-page evidence brief on [topic] for team meeting.

Audience: [OTs/interdisciplinary team/administrators]
Topic: [Intervention/assessment/practice change]
Purpose: [Propose adoption/education/policy change]

Format as:

WHAT WE KNOW (Evidence Summary)
- Finding 1: [Key evidence with strength of evidence]
- Finding 2: [Key evidence]
- Finding 3: [Key evidence]
[3-5 key findings maximum]

WHY IT MATTERS (Clinical Relevance)
- Impact on patient outcomes: [Specific benefits]
- Relevance to our population: [Why this matters here]
- Benefits vs. current practice: [What changes]

HOW TO IMPLEMENT (Action Steps)
- Practical application: [How to do this]
- Training needed: [What staff need to learn]
- Resources required: [Equipment, time, budget]
- Timeline: [Realistic implementation plan]

MEASURING SUCCESS (Outcomes)
- What to track: [Specific metrics]
- Expected results: [What success looks like]
- Timeline for seeing results: [When to evaluate]

BOTTOM LINE (Take-Home)
- Should we adopt this? [Clear recommendation]
- Level of evidence: [Grade]
- Recommendation strength: [Strong/Moderate/Weak]

KEY CITATIONS:
- [3-5 most important references]

VISUAL ELEMENT:
- [Describe table or infographic to include - outcomes data, algorithm, etc.]

Reading time: 5 minutes
Make it compelling, clear, and actionable.`,
    tags: ['evidence summary', 'presentation', 'in-service'],
    difficulty: 'Advanced'
  },
  {
    id: 'research-006',
    title: 'Critical Appraisal of Study',
    category: 'Research & Evidence',
    description: 'Evaluate research quality and applicability',
    prompt: `Help me critically appraise this study: [Citation or description]

Evaluate:

VALIDITY (Is it believable?)
- Was assignment to groups randomized?
- Were all participants accounted for at conclusion?
- Were groups similar at start?
- Were assessors blinded to group assignment?
- Were outcomes measured appropriately?
- Was analysis appropriate?

RELIABILITY (Can I trust it?)
- Sample size adequate? [Power calculation]
- Appropriate statistical tests used?
- Results make biological/clinical sense?
- Conflicts of interest addressed?
- Funding source noted?

APPLICABILITY (Can I use it?)
- Similar patients to mine? [Compare populations]
- Feasible intervention in my setting?
- Outcomes meaningful to my practice?
- Benefits worth the effort/cost?
- Harms considered?

IMPACT (How big is the effect?)
- Statistically significant? [P-values]
- Clinically meaningful? [Effect size]
- Effect size: [Small/Medium/Large]
- Number needed to treat (NNT): [If applicable]
- Confidence intervals: [Range of likely effect]

RED FLAGS:
- Signs this might not be trustworthy
- Important limitations to consider
- Bias concerns

LEVEL OF EVIDENCE:
- Study design hierarchy position
- Overall quality grade

VERDICT:
- Apply to practice immediately?
- Consider but need more evidence?
- Wait for better research?
- Don't use - insufficient evidence?

Make it practical for busy clinician review - focus on clinical usefulness.`,
    tags: ['critical appraisal', 'research quality', 'evidence'],
    difficulty: 'Advanced'
  },
  {
    id: 'research-007',
    title: 'Evaluating Claims and Trends',
    category: 'Research & Evidence',
    description: 'Separate evidence from marketing hype',
    prompt: `Help me evaluate this claim: [Intervention] works for [condition].

Source: [Where I heard this - conference, colleague, social media, advertisement]

Please help me investigate:

IS THERE EVIDENCE?
- What does research actually say?
- Quality of evidence available?
- Consistency across studies?
- Publication bias concerns?

CLAIMS VS. REALITY:
- What's being claimed: [Specific promises]
- What evidence supports: [What studies show]
- What's exaggerated: [Marketing vs. facts]
- What's unknown: [Gaps in research]

MECHANISM:
- Plausible biological/therapeutic rationale?
- How is it supposed to work?
- Does the mechanism make sense?
- Or does it sound too good to be true?

RISKS & CONSIDERATIONS:
- Potential harms or side effects
- Contraindications
- Cost (time, money, effort)
- Opportunity cost: [Time/money spent on this vs. proven approaches]

RED FLAGS:
- [Warning signs this might be a fad or pseudoscience]
- [Marketing language that raises concerns]

EXPERT CONSENSUS:
- What do professional organizations say?
- Are experts recommending this?
- Or are experts skeptical?

BOTTOM LINE CLASSIFICATION:
- Evidence-based: Strong support, should use
- Emerging: Some promising research, worth watching
- Theoretical: Plausible but unproven, needs research
- Questionable: Lacking evidence or rationale, be cautious
- Debunked: Evidence shows it doesn't work, avoid

RECOMMENDATION:
- [Should I try this with my patients?]
- [What would make me more confident?]
- [How to explain to patients asking about it?]

Help me stay evidence-based and avoid fads.`,
    tags: ['critical thinking', 'fads', 'evidence evaluation'],
    difficulty: 'Intermediate'
  },
  {
    id: 'research-008',
    title: 'Research-to-Practice Implementation',
    category: 'Research & Evidence',
    description: 'Translate research into clinical practice',
    prompt: `I want to implement this evidence-based practice: [Intervention/approach]

Evidence summary:
- [Key findings from research]
- [Recommended parameters - dosage, frequency, etc.]

My setting constraints:
- Time per session: [Duration available]
- Equipment available: [Resources on hand]
- Staff training level: [Current knowledge/skills]
- Patient population: [Who I serve]
- Organizational support: [Level of buy-in]
- Budget: [Financial constraints]

Help me create implementation plan:

ADAPTATION STRATEGY:
- Core components to maintain: [Essential elements from research]
- What can be modified: [Flexible aspects]
- How to adapt for my setting: [Specific changes]
- Maintaining fidelity: [Ensure it still works]

TRAINING NEEDS:
- What I/team need to learn: [Knowledge/skills]
- How to get training: [Resources, courses, mentorship]
- Practice needed before implementation: [Competency building]
- Competency markers: [How to know we're ready]

PILOT PHASE:
- Start with: [Which patients, how many]
- Duration: [How long to test]
- What to track: [Metrics to monitor]
- Decision point: [When to evaluate if it's working]

OUTCOME MEASUREMENT:
- How to track effectiveness: [Specific measures]
- Data collection method: [Feasible tracking]
- Documentation: [How to record in notes]

TROUBLESHOOTING:
- Anticipated barriers: [Likely challenges]
- Solutions: [How to address]
- When to adjust: [Red flags]
- Who to consult: [Support/mentorship]

FULL IMPLEMENTATION TIMELINE:
- Phase 1 (Pilot): [Timeframe and goals]
- Phase 2 (Expansion): [Timeframe and goals]
- Phase 3 (Standard practice): [When it becomes routine]
- Review and refine: [Ongoing quality improvement]

SUSTAINABILITY:
- How to maintain over time
- Training new staff
- Ongoing competency

Make it practical, realistic, and achievable for real-world clinical setting.`,
    tags: ['implementation', 'evidence to practice', 'quality improvement'],
    difficulty: 'Advanced'
  },

  // ==========================================
  // ADMINISTRATIVE (6 prompts)
  // ==========================================
  {
    id: 'admin-001',
    title: 'Productivity Analysis and Planning',
    category: 'Administrative',
    description: 'Analyze and improve clinical productivity',
    prompt: `Help me analyze and improve my productivity.

Current situation:
- Setting: [Outpatient/SNF/Hospital/Schools/etc.]
- Productivity target: [Percentage or units expected]
- Current productivity: [What I'm achieving]
- Scheduled hours: [Work hours available]
- Documentation time: [How much time spent on notes]

Challenges:
- [Specific barriers to productivity]

Please help with:

PRODUCTIVITY ANALYSIS:
- Where time is going: [Break down current time use]
- Productive vs. non-productive time: [Analysis]
- Gaps: [Why falling short of target]

TIME OPTIMIZATION STRATEGIES:
- Scheduling: [How to structure day for better flow]
- Documentation: [Efficient note-writing strategies]
- Treatment planning: [Time-saving approaches]
- Reduce transition time: [Between patients/tasks]

REALISTIC SCHEDULE:
- [Sample schedule showing how to hit target]
- Treatment time blocks: [When to see patients]
- Documentation time: [When to write notes]
- Prep/cleanup: [Built-in time]
- Flexibility: [Buffer for unexpected]

DOCUMENTATION EFFICIENCY:
- Templates: [Standardize common notes]
- AI assistance: [How to use ethically]
- Write during session: [Real-time documentation tips]
- Reduce redundancy: [Streamline process]

RED FLAGS TO AVOID:
- Don't sacrifice quality for productivity
- Maintain ethical documentation
- Avoid burnout
- When to address systemic issues vs. personal efficiency

ACTION PLAN:
- Week 1: [Changes to implement]
- Week 2: [Additional changes]
- Week 3-4: [Evaluate and adjust]

Success measures:
- [How to track improvement]
- [Realistic timeline for change]

Help me be efficient without compromising patient care or my wellbeing.`,
    tags: ['productivity', 'time management', 'efficiency'],
    difficulty: 'Intermediate'
  },
  {
    id: 'admin-002',
    title: 'Program Development Proposal',
    category: 'Administrative',
    description: 'Propose new OT program or service line',
    prompt: `Help me create a proposal for new OT program: [Program description]

Program overview:
- Service: [What we'll provide]
- Population: [Who we'll serve]
- Setting: [Where this will happen]

Create business proposal:

EXECUTIVE SUMMARY:
- [One-paragraph overview of program and value]

NEEDS ASSESSMENT:
- Population need: [Data showing demand]
- Gap in current services: [What's missing]
- Community/facility need: [Why this matters]

PROGRAM DESCRIPTION:
- Services provided: [Specific offerings]
- Staff required: [FTE, credentials]
- Space/equipment needed: [Resources]
- Hours of operation: [Schedule]

TARGET OUTCOMES:
- Patient outcomes: [Clinical benefits]
- Volume projections: [How many patients]
- Quality metrics: [How to measure success]

FINANCIAL ANALYSIS:

Start-up costs:
- Equipment: [$Amount]
- Space modifications: [$Amount]
- Marketing: [$Amount]
- Training: [$Amount]
Total: [$Amount]

Ongoing costs (annual):
- Staff: [$Amount]
- Supplies: [$Amount]
- Overhead: [$Amount]

Revenue projections:
- Year 1: [$Amount] - [Assumptions]
- Year 2: [$Amount]
- Year 3: [$Amount]

Break-even analysis:
- [When program becomes profitable]

COMPETITIVE ANALYSIS:
- Similar programs in area: [What exists]
- Our differentiation: [Why ours is better/different]
- Referral sources: [Where patients come from]

IMPLEMENTATION TIMELINE:
- Planning phase: [Duration and tasks]
- Setup phase: [What needs to happen]
- Soft launch: [Initial start]
- Full operations: [When fully running]

RISK ASSESSMENT:
- Potential challenges: [What could go wrong]
- Mitigation strategies: [How to address]

SUCCESS METRICS:
- [What to track to show program is working]
- [Review points - 3 months, 6 months, 1 year]

Make it compelling and data-driven for administrative approval.`,
    tags: ['program development', 'business proposal', 'admin'],
    difficulty: 'Advanced'
  },
  {
    id: 'admin-003',
    title: 'Staff Meeting Agenda',
    category: 'Administrative',
    description: 'Structure productive team meetings',
    prompt: `Create agenda for OT staff meeting.

Meeting purpose: [Regular staff meeting/specific issue/planning session]
Duration: [Time available]
Attendees: [Who will be there]

Topics to cover:
- [List key items to address]

Create structured agenda:

BEFORE MEETING:
- Pre-read materials: [What to send ahead]
- Preparation needed: [What attendees should bring]

AGENDA:

Welcome & Attendance (5 min)
- Quick check-in

Review Action Items from Last Meeting (10 min)
- [Previous commitments - status update]

Topic 1: [Name] (Time allocated: ___ min)
- Purpose: [Why we're discussing]
- Outcome needed: [Decision/brainstorm/information]
- Discussion points:
  • [Point to cover]
  • [Point to cover]
- Action items: [Who will do what by when]

[Repeat structure for each topic]

Announcements & Updates (10 min)
- [Important information to share]

Questions/Open Forum (10 min)
- [Space for other items]

Next Steps & Closing (5 min)
- Recap action items
- Next meeting date

MEETING NORMS:
- Start/end on time
- [Other ground rules]

FOLLOW-UP:
- Meeting notes: [Who will send, by when]
- Action items tracking: [How to follow up]

Attachments to send ahead:
- [List any documents to include]

Make it efficient, focused, and productive. Time allocations should ensure meeting stays on track.`,
    tags: ['meetings', 'team management', 'agenda'],
    difficulty: 'Beginner'
  },
  {
    id: 'admin-004',
    title: 'Student Supervision Planning',
    category: 'Administrative',
    description: 'Structure fieldwork student experience',
    prompt: `Plan supervision for OT student fieldwork placement.

Student: Level I / Level II
Duration: [Weeks]
Setting: [Your practice setting]
Student experience level: [What they've done before]

Create fieldwork plan:

WEEK-BY-WEEK LEARNING PROGRESSION:

Week 1: Orientation & Observation
- Objectives: [What student should learn]
- Activities:
  • [Specific tasks - observation, shadowing]
  • [Introductions, facility tour]
- Caseload: [None/minimal observation]
- Meetings: [Orientation meetings]
- Assignments: [What to complete]

Week 2: Guided Practice
- Objectives: [Progressive learning goals]
- Activities:
  • [Assist with evaluations]
  • [Co-treat sessions]
- Caseload: [How many patients, what supervision level]
- Skills to practice: [Specific competencies]

[Continue week by week through placement]

Final Week: Independence & Transition
- Objectives: [Demonstrate competence]
- Caseload: [Independent with supervision available]
- Final evaluation
- Wrap-up

SUPERVISION STRUCTURE:

Daily:
- Brief check-in: [Morning planning]
- Real-time feedback: [During patient care]
- End-of-day debrief: [Reflection]

Weekly:
- Formal supervision meeting: [Review progress, set goals]
- Midterm evaluation: [Week ___]
- Final evaluation: [Last week]

LEARNING ACTIVITIES:

Clinical skills:
- [Specific evaluations to learn]
- [Intervention techniques to practice]
- [Documentation practice]

Professional development:
- [Attend team meetings]
- [Interact with other disciplines]
- [Professional communication]

Assignments:
- Case study presentation: [Due week ___]
- Journal reflections: [Weekly]
- [Other projects]

EVALUATION CRITERIA:
- [How student will be graded/assessed]
- [Key competencies to achieve]
- [Red flags for struggling student]

SUPPORT FOR STRUGGLING STUDENT:
- [How to identify issues early]
- [Intervention strategies]
- [When to involve academic coordinator]

RESOURCES FOR STUDENT:
- [Reading materials]
- [Protocols/guidelines to review]
- [Who to ask for help]

Create supportive, structured learning environment that progresses student from dependent to independent practice.`,
    tags: ['fieldwork', 'student supervision', 'clinical education'],
    difficulty: 'Advanced'
  },
  {
    id: 'admin-005',
    title: 'Quality Improvement Project',
    category: 'Administrative',
    description: 'Design QI initiative for OT services',
    prompt: `Design quality improvement project for: [Area to improve]

Current situation:
- Problem: [What's not working well]
- Current performance: [Data/metrics]
- Target: [Where we want to be]

Create QI project plan:

PROBLEM STATEMENT:
- [Clear description of issue]
- [Impact on patients, staff, or outcomes]
- [Why this matters]

AIM STATEMENT:
"We aim to [improve what] from [current state] to [target state] by [timeline] as measured by [metrics]."

BASELINE DATA:
- Current performance: [Numbers/percentages]
- How measured: [Data source]
- Time period: [When data collected]

ROOT CAUSE ANALYSIS:
- Why is this happening? [Identify causes]
- Contributing factors: [List]
- Fishbone/5 Whys analysis: [Method used]

CHANGE IDEAS:
Potential improvements to test:
1. [Change idea 1]
   - How this addresses root cause
   - Feasibility
2. [Change idea 2]
3. [Change idea 3]

PLAN-DO-STUDY-ACT (PDSA) CYCLES:

PDSA Cycle 1:
- Plan: [What we'll test, prediction]
- Do: [How we'll test it, who, when, where]
- Study: [How we'll measure results]
- Act: [Adopt/Adapt/Abandon based on results]

[Additional PDSA cycles as needed]

MEASUREMENT PLAN:

Outcome measures:
- [Impact on patient outcomes]

Process measures:
- [Are we doing what we planned?]

Balancing measures:
- [Unintended consequences to watch for]

Data collection:
- Who: [Responsible person]
- How: [Method]
- Frequency: [How often]

IMPLEMENTATION TIMELINE:
- Month 1: [Activities]
- Month 2: [Activities]
- Month 3: [Review and spread]

BARRIERS & MITIGATION:
- Potential barrier 1: [Solution]
- Potential barrier 2: [Solution]

STAKEHOLDER ENGAGEMENT:
- Who needs to be involved: [List]
- How to get buy-in: [Strategies]

SUSTAINABILITY PLAN:
- How to maintain improvements long-term
- Training new staff
- Ongoing monitoring

SUCCESS DEFINITION:
- [What success looks like]
- [How we'll know we achieved it]

Create systematic, data-driven improvement plan.`,
    tags: ['quality improvement', 'QI', 'process improvement'],
    difficulty: 'Advanced'
  },
  {
    id: 'admin-006',
    title: 'Budget Justification for Equipment/Program',
    category: 'Administrative',
    description: 'Make financial case for resources',
    prompt: `Create budget justification for: [Equipment/program/staff request]

Request: [What you're asking for]
Total cost: [$Amount]
Requesting from: [Decision maker/committee]

Build justification:

EXECUTIVE SUMMARY:
- What: [Brief description]
- Why: [One-paragraph rationale]
- Cost: [Total investment]
- ROI: [Return on investment summary]

NEEDS ASSESSMENT:

Current gaps:
- [What we can't do now]
- [Impact on patients]
- [Impact on outcomes]
- [Impact on efficiency]

Data supporting need:
- [Patient volume that would benefit]
- [Outcomes data]
- [Referral opportunities]

PROPOSED SOLUTION:
- [Detailed description of request]
- [How it addresses gaps]
- [Benefits to patients, staff, organization]

FINANCIAL ANALYSIS:

One-time costs:
- Purchase/setup: [$Amount]
- Training: [$Amount]
- Installation: [$Amount]
Total initial: [$Amount]

Ongoing costs (annual):
- Maintenance: [$Amount]
- Supplies: [$Amount]
- Staff time: [$Amount]

Revenue generation:
- Additional billable units: [Calculation]
- New patient volume: [Projection]
- Reimbursement rates: [Amounts]
- Annual revenue: [$Amount]

Cost-benefit analysis:
- Break-even point: [When]
- ROI: [Percentage and timeframe]
- Payback period: [Duration]

QUALITATIVE BENEFITS:

Patient care:
- [Improved outcomes]
- [Better patient experience]
- [Expanded services]

Competitive advantage:
- [What this enables that others don't have]
- [Marketing value]

Staff satisfaction:
- [How this helps therapists]
- [Recruitment/retention value]

Quality/safety:
- [Risk reduction]
- [Compliance improvements]

ALTERNATIVES CONSIDERED:
- Option 1: [Alternative - why not chosen]
- Option 2: [Alternative - why not chosen]
- Do nothing: [Consequences]

IMPLEMENTATION PLAN:
- Timeline: [Purchase to full implementation]
- Training: [What's needed]
- Integration: [How it fits into current workflow]

RISK ASSESSMENT:
- Risks of approving: [Potential downsides]
- Risks of not approving: [Lost opportunities]

SUCCESS METRICS:
- [How we'll measure if investment was worth it]
- [Review timeline]

RECOMMENDATION:
[Strong, clear request for approval with compelling rationale]

Make it financially sound and aligned with organizational priorities.`,
    tags: ['budget', 'financial justification', 'business case'],
    difficulty: 'Advanced'
  },

  // ==========================================
  // PROFESSIONAL DEVELOPMENT (4 prompts)
  // ==========================================
  {
    id: 'prof-001',
    title: 'Continuing Education Plan',
    category: 'Professional Development',
    description: 'Strategic professional development planning',
    prompt: `Help me create a professional development plan.

Current role: [Your position]
Career goals: [Where you want to go]
Time available: [For learning]
Budget: [For CEUs/courses]
Learning style: [Preferences]

Areas to develop:
- [Skill or knowledge gap 1]
- [Skill or knowledge gap 2]
- [Skill or knowledge gap 3]

Create development plan:

SHORT-TERM (Next 6 months):

Goal 1: [Specific skill to develop]
- Why: [How this supports your goals]
- Learning activities:
  • Course: [Specific course or CEU]
    - Source: [Where to take it]
    - Cost: [$Amount]
    - Time: [Hours required]
  • Practice: [How to apply new skills]
  • Mentorship: [Who to learn from]
- Success metric: [How you'll know you've learned it]
- Timeline: [By when]

[Repeat for 2-3 short-term goals]

MEDIUM-TERM (6-12 months):
- [Skills to develop]
- [Certifications to pursue]
- [Experiences to gain]

LONG-TERM (1-3 years):
- [Advanced certifications]
- [Specialty development]
- [Leadership skills]

LEARNING ACTIVITIES BY TYPE:

Formal education:
- [Courses, certifications to pursue]

Self-directed learning:
- [Books, podcasts, articles to explore]
- [Online resources]

Experiential learning:
- [New patient populations to work with]
- [Techniques to try]
- [Projects to lead]

Mentorship/networking:
- [People to connect with]
- [Professional groups to join]
- [Conferences to attend]

BUDGET ALLOCATION:
- High priority: [CEUs/courses - $amount]
- Medium priority: [Resources - $amount]
- Nice to have: [Conferences - $amount]

TIME ALLOCATION:
- Weekly: [Hours for learning]
- Monthly: [Dedicated development time]
- Quarterly: [Major learning activities]

TRACKING PROGRESS:
- [How to monitor development]
- [Review points - quarterly, annually]
- [Adjust plan based on changes]

Make it strategic, achievable, and aligned with career goals.`,
    tags: ['professional development', 'CEU', 'career planning'],
    difficulty: 'Intermediate'
  },
  {
    id: 'prof-002',
    title: 'Conference Presentation Preparation',
    category: 'Professional Development',
    description: 'Prepare engaging professional presentations',
    prompt: `Help me prepare a conference presentation on [topic].

Presentation details:
- Conference: [Name]
- Audience: [Who will attend - students, experienced OTs, interdisciplinary]
- Duration: [Time slot]
- Format: [Lecture, workshop, poster]
- Learning level: [Beginner/Intermediate/Advanced]

Topic: [Specific presentation topic]

Create presentation plan:

LEARNING OBJECTIVES (3-5):
Participants will be able to:
1. [Specific, measurable objective]
2. [Another objective]
3. [Third objective]

PRESENTATION OUTLINE:

Introduction (5-10% of time):
- Hook: [Engaging opening]
- Why this matters: [Relevance]
- Learning objectives: [What they'll gain]

Main Content (70-80% of time):

Section 1: [Topic]
- Key points: [3-4 main ideas]
- Evidence: [Research to cite]
- Examples: [Real-world application]
- Activity: [Engagement strategy - if workshop]

Section 2: [Topic]
- [Key content]

Section 3: [Topic]
- [Key content]

Conclusion (10-15% of time):
- Summary: [Key takeaways]
- Application: [How to use this tomorrow]
- Resources: [Handouts, references]
- Q&A

ENGAGEMENT STRATEGIES:
- [Interactive elements to include]
- [Questions to ask audience]
- [Cases to discuss]
- [Demonstrations]

VISUAL AIDS:
Slide 1: Title
- [Content suggestions]

Slide 2-3: Introduction
- [Visual ideas]

[Continue through presentation]

Design principles:
- Minimal text per slide
- Strong visuals
- Consistent design
- Accessible (readable, high contrast)

HANDOUT MATERIALS:
- One-page summary: [Key points]
- Resources list: [References, tools]
- Take-home activity: [How to implement]

PRACTICE PLAN:
- Run-through 1: [Just you - timing]
- Run-through 2: [Colleague feedback]
- Run-through 3: [Final polish]

ANTICIPATE QUESTIONS:
- Likely question 1: [How to answer]
- Challenging question: [How to respond]
- Out of scope question: [How to redirect]

DAY-OF CHECKLIST:
- [Equipment to bring]
- [Tech to test]
- [Materials to print]

EVALUATION:
- [How to get feedback from attendees]
- [Self-reflection questions]

Make it engaging, evidence-based, and immediately applicable for participants.`,
    tags: ['presentation', 'conference', 'public speaking'],
    difficulty: 'Advanced'
  },
  {
    id: 'prof-003',
    title: 'Research Project Design',
    category: 'Professional Development',
    description: 'Design feasible clinical research study',
    prompt: `Help me design a research study on [topic].

Research interest: [What I want to investigate]
Setting: [Where I practice]
Resources: [Time, funding, support available]
Research experience: [My background level]

Design study:

RESEARCH QUESTION:
- PICO format:
  • Population: [Who]
  • Intervention: [What]
  • Comparison: [Compared to what]
  • Outcome: [What you're measuring]

Research question: [Clear, answerable question]

BACKGROUND & SIGNIFICANCE:
- What's already known: [Brief literature summary]
- Gap this fills: [What's missing in research]
- Why this matters: [Clinical significance]

STUDY DESIGN:
- Type: [RCT, cohort, case series, single-subject, etc.]
- Justification: [Why this design]
- Setting: [Where conducted]
- Duration: [Study timeline]

METHODS:

Participants:
- Inclusion criteria: [Who can participate]
- Exclusion criteria: [Who cannot]
- Sample size: [How many needed - justify]
- Recruitment: [How to find participants]

Intervention:
- Experimental group: [What they get]
- Control/comparison: [Alternative]
- Dosage/frequency: [Specific parameters]
- Fidelity monitoring: [Ensure consistency]

Outcome Measures:
- Primary outcome: [Main measure]
- Secondary outcomes: [Additional measures]
- Why chosen: [Psychometric properties]
- When measured: [Baseline, intervals, endpoint]

Data Collection:
- Who collects: [Assessors]
- Training: [Ensuring reliability]
- Blinding: [If applicable]
- Timeline: [Collection schedule]

ANALYSIS PLAN:
- Statistical tests: [What you'll use]
- Significance level: [P < .05]
- Software: [SPSS, R, etc.]
- Intent-to-treat vs. per-protocol: [Approach]

ETHICAL CONSIDERATIONS:
- IRB approval: [Requirements]
- Informed consent: [Process]
- Confidentiality: [Protection measures]
- Risks/benefits: [For participants]
- Withdrawal: [Right to leave study]

FEASIBILITY:
- Timeline: [Realistic project timeline]
- Resources needed: [Equipment, staff, funding]
- Potential challenges: [What could go wrong]
- Mitigation: [How to address]

DISSEMINATION PLAN:
- Conference presentation: [Which venues]
- Publication: [Target journals]
- Clinical application: [How to use findings]

BUDGET (if needed):
- Personnel: [$Amount]
- Equipment: [$Amount]
- Participant incentives: [$Amount]
- Total: [$Amount]

Make it feasible for busy clinician-researcher in real-world setting.`,
    tags: ['research', 'study design', 'scholarship'],
    difficulty: 'Advanced'
  },
  {
    id: 'prof-004',
    title: 'Career Advancement Strategy',
    category: 'Professional Development',
    description: 'Plan strategic career moves',
    prompt: `Help me create a career advancement strategy.

Current role: [Position, years of experience]
Career goals: [Where you want to be in 3-5 years]
Strengths: [What you're good at]
Development areas: [What you need to improve]
Constraints: [Geographic, family, financial considerations]

Create strategic plan:

GOAL CLARIFICATION:

Ultimate career target:
- Position: [Specific role desired]
- Setting: [Where you want to work]
- Timeline: [When you want to achieve this]
- Why: [What attracts you to this goal]

PATHWAY ANALYSIS:

Typical path to this role:
- Common progression: [Usual steps]
- Required credentials: [Certifications, degrees]
- Experience needed: [Years, settings, populations]
- Skills required: [Clinical, leadership, business]

Your current position:
- How far from goal: [Gap analysis]
- Advantages you have: [Leverage points]
- Gaps to fill: [What's missing]

STRATEGIC ACTION PLAN:

Year 1:
- Experience to gain:
  • [Specific clinical experience]
  • [Leadership opportunity]
  • [Project to lead]
- Credentials to pursue:
  • [Certification or training]
- Network building:
  • [Professional connections to make]
  • [Groups to join]
- Visibility:
  • [How to become known in field]

Year 2:
- [Progressive steps]
- [Increased responsibility]
- [Advanced credentials]

Year 3-5:
- [Final preparation for target role]
- [Application/transition strategy]

SKILL DEVELOPMENT:

Clinical expertise:
- [Specialization to develop]
- [Advanced techniques to master]

Leadership skills:
- [Management training]
- [Project leadership experience]
- [Mentoring others]

Business acumen:
- [Financial skills]
- [Program development]
- [Strategic planning]

Communication:
- [Presentation skills]
- [Writing/publishing]
- [Professional presence]

NETWORKING STRATEGY:

Internal networking:
- [Build relationships within organization]
- [Demonstrate value]
- [Advocate for advancement]

External networking:
- [Professional organizations]
- [Mentors to cultivate]
- [Conferences/events to attend]

Online presence:
- [LinkedIn optimization]
- [Professional writing/blogging]
- [Thought leadership]

VISIBILITY BUILDING:

Achievements to highlight:
- [Projects to lead and publicize]
- [Awards to pursue]
- [Publications/presentations]

Documentation:
- [Portfolio development]
- [Track accomplishments]
- [Collect testimonials]

TIMING & READINESS:

When to make moves:
- [Signals you're ready]
- [Best timing for transitions]
- [How long to stay in current role]

Opportunity watching:
- [Where to look for openings]
- [How to learn about opportunities]
- [Building pipeline]

CONTINGENCY PLANNING:

If direct path doesn't open:
- Alternative path 1: [Lateral move option]
- Alternative path 2: [Different route to goal]

If goals change:
- [How to pivot]
- [Transferable skills]

SUPPORT SYSTEM:

Mentors/advisors:
- [Who can guide you]
- [How to engage them]

Sponsors:
- [Who can advocate for your advancement]

Professional development:
- [Coaches, consultants to work with]

Make it strategic, realistic, and actionable for achieving career goals.`,
    tags: ['career advancement', 'leadership', 'professional growth'],
    difficulty: 'Advanced'
  },

  // ==========================================
  // CASE ANALYSIS (4 prompts)
  // ==========================================
  {
    id: 'case-001',
    title: 'Complex Case Consultation',
    category: 'Case Analysis',
    description: 'Get insights on challenging patient cases',
    prompt: `Help me think through this complex case.

Patient profile:
- Age: [Age]
- Diagnosis/diagnoses: [Primary and comorbid conditions]
- Setting: [Where you're seeing them]
- Referral reason: [Why referred to OT]

Complexity factors:
- [What makes this case challenging]
- [Multiple diagnoses, social factors, etc.]

Current status:
- Functional abilities: [What they can do]
- Limitations: [What they struggle with]
- Cognition: [Relevant cognitive issues]
- Psychosocial: [Mood, motivation, support system]

Treatment so far:
- What we've tried: [Interventions attempted]
- Response: [What's worked, what hasn't]

Current challenges:
- [Specific problem you're trying to solve]

Please help with:

CLINICAL REASONING:

What might be going on?
- [Hypotheses about underlying issues]
- [Consideration of all ICF components]
- [Psychosocial factors affecting performance]

What am I missing?
- [Alternative perspectives]
- [Blind spots to consider]
- [Additional assessments that might help]

ASSESSMENT RECOMMENDATIONS:
- [Specific evals to clarify picture]
- [What to look for]
- [How to assess complex factors]

INTERVENTION IDEAS:

Immediate strategies:
- [What to try in next few sessions]
- [How to address current barrier]

Medium-term plan:
- [Progression over weeks]
- [Building on small successes]

Addressing complexity:
- [Managing multiple issues]
- [Prioritization guidance]
- [Coordination with other disciplines]

EVIDENCE BASE:
- [Research relevant to this case]
- [Best practice for similar cases]
- [Level of evidence available]

REALISTIC GOALS:
- [What's achievable given complexity]
- [Timeframe for progress]
- [Success metrics]

TEAM COLLABORATION:
- [Who else to involve]
- [What to communicate to team]
- [Coordination needs]

FAMILY/CAREGIVER CONSIDERATIONS:
- [Education needed]
- [Support mobilization]
- [Addressing family dynamics]

DISCHARGE PLANNING:
- [Likely discharge destination]
- [What needs to be in place]
- [Long-term management]

ETHICAL CONSIDERATIONS:
- [Autonomy vs. safety]
- [Quality of life discussions]
- [Competing priorities]

Provide thorough clinical reasoning support for complex decision-making.`,
    tags: ['case consultation', 'clinical reasoning', 'complex case'],
    difficulty: 'Advanced'
  },
  {
    id: 'case-002',
    title: 'Differential Diagnosis - OT Perspective',
    category: 'Case Analysis',
    description: 'Analyze functional limitations to guide assessment',
    prompt: `Help me think through differential diagnosis for this functional limitation.

Patient presents with:
- Chief complaint: [What patient/referral says is the problem]
- Observable limitation: [What you see them struggle with]
- Context: [When it happens, when it doesn't]

Background:
- Medical diagnosis: [If known]
- Relevant history: [Pertinent information]
- Medications: [If relevant to function]

Initial observations:
- [What you've noticed]

From OT functional lens, help me consider:

POSSIBLE UNDERLYING FACTORS:

Physical/Motor:
- Strength: [Could this be weakness? Where?]
- Range of motion: [Limitation possible?]
- Coordination: [Fine or gross motor issue?]
- Endurance: [Fatigue factor?]
- Pain: [Could pain be limiting?]
- Sensation: [Sensory deficit?]

Cognitive/Perceptual:
- Attention: [Distractibility affecting performance?]
- Memory: [Forgetting steps?]
- Executive function: [Planning/sequencing issue?]
- Visual perception: [Spatial, figure-ground?]
- Praxis: [Motor planning difficulty?]

Psychosocial:
- Motivation: [Lacking drive?]
- Depression/anxiety: [Mood affecting function?]
- Self-efficacy: [Fear or lack of confidence?]
- Learned helplessness: [Giving up too easily?]

Environmental:
- Physical barriers: [Space, accessibility issues?]
- Social: [Lack of support or over-help?]
- Cultural: [Values affecting participation?]

ASSESSMENT STRATEGY:

Priority assessments to rule in/out:
1. [First assessment - what it will tell you]
2. [Second assessment]
3. [Third assessment]

Observation-based evaluation:
- [What to watch for during task performance]
- [Cues that point to different causes]

Functional testing:
- [Real-world tasks to try]
- [What performance patterns reveal]

PATTERN RECOGNITION:

If it's primarily [factor], I would expect to see:
- [Associated signs]
- [Performance pattern]

If it's primarily [different factor], I would see:
- [Different associated signs]
- [Different pattern]

TREATMENT IMPLICATIONS:

If underlying cause is [factor]:
- Intervention: [What to do]
- Prognosis: [Expected outcome]

If cause is [different factor]:
- Intervention: [Different approach]
- Prognosis: [Different expectation]

NEXT STEPS:
- [What to do in next session to clarify]
- [Who to consult]
- [Information to gather]

Help me systematically rule in/out causes to target treatment effectively.`,
    tags: ['differential diagnosis', 'assessment', 'clinical reasoning'],
    difficulty: 'Advanced'
  },
  {
    id: 'case-003',
    title: 'Discharge Planning - Complex Case',
    category: 'Case Analysis',
    description: 'Plan safe, realistic discharge for challenging situations',
    prompt: `Help me plan discharge for complex situation.

Patient:
- Age: [Age]
- Diagnosis: [Condition]
- Current setting: [Hospital, SNF, etc.]
- Proposed discharge to: [Home, facility, etc.]
- Timeline pressure: [Expected discharge date]

Functional status:
- ADL level: [Current independence]
- Mobility: [Ambulatory, wheelchair, etc.]
- Cognition: [Alertness, memory, safety awareness]
- Medical needs: [Ongoing care requirements]

Living situation:
- Home setup: [Accessibility, stairs, bathroom, etc.]
- Lives with: [Who's at home]
- Caregiver availability: [Who can help, how much]
- Financial resources: [Insurance, private pay, limitations]

Concerns:
- [Safety issues]
- [Gaps in support]
- [Complexity factors]

Help me with:

SAFETY ASSESSMENT:

Can patient safely discharge to [proposed setting]?
- Physical safety: [Fall risk, medical emergencies]
- Self-care safety: [Food, meds, hygiene]
- Environmental safety: [Home hazards]
- Judgment/cognition: [Decision-making ability]

Red flags:
- [Concerning factors]

Mitigation strategies:
- [How to address safety concerns]

REALISTIC FUNCTION AT DISCHARGE:

What patient will realistically be able to do:
- [Specific ADLs with level of assistance]
- [IADLs with support needed]
- [Mobility with devices/assistance]

What patient WON'T be able to do:
- [Tasks requiring help or alternatives]

SUPPORT NEEDS:

Human support:
- Caregiver requirements: [How much help needed when]
- Who provides: [Family, paid caregiver, home health]
- Caregiver capacity: [Can they manage this?]
- Backup plan: [If primary caregiver unavailable]

Services needed:
- Home health: [OT, PT, nursing - how often]
- Home care aide: [Hours per week]
- Meal delivery: [If cooking unsafe]
- Transportation: [To appointments]
- Other: [Specific needs]

EQUIPMENT & MODIFICATIONS:

Must have (safety critical):
- [Essential equipment - walker, shower chair, etc.]
- [Critical modifications - grab bars, ramp]
- How to obtain: [Funding, sources]

Should have (function/quality of life):
- [Helpful equipment]
- [Nice-to-have modifications]

DISCHARGE PLAN:

Immediate (before discharge):
- [ ] Equipment ordered and delivered
- [ ] Home modifications completed
- [ ] Services arranged and scheduled
- [ ] Caregiver trained on: [specific tasks]
- [ ] Emergency plan established
- [ ] Medications organized
- [ ] Follow-up appointments scheduled
- [ ] [Other essential tasks]

First week home:
- Support level: [Who helps with what]
- Service frequency: [Home health schedule]
- Check-ins: [Phone calls, visits]

First month:
- Expected progress: [Function should improve to...]
- Service adjustments: [Decrease help as able]
- Problem-solving: [Address issues that arise]

CONTINGENCY PLANNING:

If discharge to [setting] isn't safe:
- Alternative 1: [Different placement]
- Alternative 2: [Additional time in current setting]
- What needs to change: [To make discharge safe]

Red flags after discharge:
- [Warning signs of problems]
- [When to readmit]

TEAM COMMUNICATION:

Who needs to know what:
- Physician: [Medical needs, safety concerns]
- Family: [Realistic expectations, support requirements]
- Receiving facility/services: [Specific information]
- Care coordinator: [Service setup]

DOCUMENTATION:

Discharge summary must include:
- [Current function with specifics]
- [Safety considerations]
- [Equipment/modifications]
- [Service recommendations]
- [Caregiver education completed]

Create comprehensive, realistic discharge plan that maximizes safety while respecting patient/family goals.`,
    tags: ['discharge planning', 'safety', 'complex case'],
    difficulty: 'Advanced'
  },
  {
    id: 'case-004',
    title: 'Treatment Not Working - Troubleshooting',
    category: 'Case Analysis',
    description: 'Analyze why treatment isn\'t effective and pivot',
    prompt: `Help me troubleshoot - treatment isn't working as expected.

Patient: [Age, diagnosis, setting]

Original goals:
- [Goal 1]
- [Goal 2]

Treatment approach:
- [What we've been doing - specific interventions]
- [Frequency, duration]
- [Theoretical basis]

Expected progress:
- [What I thought would happen by now]

Actual progress:
- [What's actually happening]
- [Lack of progress or regression]

Time in treatment: [How long]

Help me analyze:

PROBLEM IDENTIFICATION:

Is there really no progress?
- [Objective data - what measures say]
- [Subjective reports - patient perception]
- [Have expectations been realistic?]
- [Is change happening but slower than expected?]

If truly no progress, why might that be?

POSSIBLE REASONS FOR LACK OF PROGRESS:

1. Wrong target?
- Goals appropriate?: [Are we working on what matters?]
- Underlying issue missed?: [Something else causing problem?]
- Client motivation?: [Do they care about these goals?]

2. Wrong intervention?
- Evidence-based for this patient?: [Is approach appropriate?]
- Intensity sufficient?: [Enough dosage?]
- Fidelity to protocol?: [Am I doing it correctly?]
- Theoretical mismatch?: [Wrong frame of reference?]

3. Barriers I'm missing?
- Medical: [Uncontrolled symptoms, medication effects]
- Cognitive: [Understanding, memory, awareness]
- Psychosocial: [Depression, anxiety, trauma]
- Environmental: [Home situation, resources]
- Cultural: [Values, beliefs affecting buy-in]

4. External factors?
- Outside therapy: [What happens between sessions?]
- Carryover: [Is learning generalizing?]
- Competing demands: [Other things interfering?]

5. Relationship/rapport?
- Therapeutic alliance: [Is there trust?]
- Communication: [Are we understanding each other?]
- Cultural competence: [Am I missing something?]

REASSESSMENT STRATEGY:

What to re-evaluate:
- [Specific assessments to repeat or add]
- [Questions to ask patient/family]
- [Observations to make]

Fresh eyes:
- [Case consultation with colleague]
- [What would someone else notice?]

TREATMENT PIVOTS TO CONSIDER:

Option 1: Modify current approach
- Change: [Specific adjustment]
- Rationale: [Why this might work better]
- Try for: [How long before evaluating]

Option 2: Different intervention
- Switch to: [Alternative approach]
- Evidence base: [Why this might be better]
- Trial period: [Duration]

Option 3: Change goals
- New focus: [Different priorities]
- Reason: [Why shift]

Option 4: Reduce/discontinue
- Criteria: [When to consider discharge]
- Alternatives: [What patient needs instead]

PATIENT COLLABORATION:

Questions to ask patient:
- [What's their perspective?]
- [What would help?]
- [Are goals still meaningful?]

Shared decision-making:
- [How to engage patient in problem-solving]
- [Honesty about lack of progress]

TIMELINE FOR CHANGE:

Immediate adjustments: [This week]
- [Specific changes to try]

2-4 weeks:
- [Evaluate if adjustments helping]
- [Decision point about continuing current plan]

If still no progress:
- [Major pivot needed]
- [Potential discharge discussion]

LEARNING FROM THIS:

What would I do differently next time?
- [Clinical reasoning insights]
- [Assessment I should have done sooner]
- [Red flags to watch for]

Help me critically analyze and create effective new plan.`,
    tags: ['troubleshooting', 'clinical reasoning', 'treatment modification'],
    difficulty: 'Advanced'
  }
];

// Helper functions for filtering
export const getPromptsByCategory = (category: PromptCategory): Prompt[] => {
  return prompts.filter(p => p.category === category);
};

export const getPromptsByDifficulty = (difficulty: Prompt['difficulty']): Prompt[] => {
  return prompts.filter(p => p.difficulty === difficulty);
};

export const searchPrompts = (query: string): Prompt[] => {
  const lowerQuery = query.toLowerCase();
  return prompts.filter(p =>
    p.title.toLowerCase().includes(lowerQuery) ||
    p.description.toLowerCase().includes(lowerQuery) ||
    p.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
};

export const getAllCategories = (): PromptCategory[] => {
  return Array.from(new Set(prompts.map(p => p.category)));
};
